import type { ColumnSizingState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

import type { UserStorage } from 'apis/userStorage';
import { useServerStorage } from 'utils/useServerStorage';

import type { ConversationsTableDensity } from '../conversationsTable.types';

type UserStorageConversationsTable = NonNullable<
  NonNullable<UserStorage['conversations']>['table']
>;

export type UseConversationsTableLayoutOptions = {
  /** Whether the user can customize the table; no layout is returned otherwise. */
  readonly isEnabled: boolean;
};

/** The user's saved layout of the conversations table, and how to change it. */
export type UseConversationsTableLayoutResult = {
  /** How much space the rows take up. */
  readonly density: ConversationsTableDensity;
  /** The ids of the columns in the order the user has saved, which may leave some out. */
  readonly columnOrder: UserStorageConversationsTable['columnOrder'];
  /** The column preferences, keyed by column id. */
  readonly columns: UserStorageConversationsTable['columns'];
  /** Saves how much space the rows take up. */
  readonly setDensity: (density: ConversationsTableDensity) => void;
  /** Shows or hides a column. */
  readonly setIsColumnVisible: (columnId: string, isVisible: boolean) => void;
  /** Pins a column to the start of the table, or unpins it. */
  readonly setIsColumnPinned: (columnId: string, isPinned: boolean) => void;
  /** Saves the order of the columns after the user moves one, and whether the moved column is pinned. */
  readonly setColumnOrder: (
    columnOrder: readonly string[],
    columnId: string,
    isPinned: boolean,
  ) => void;
  /** Saves the widths of the resized columns, which gives the other columns their default widths back. */
  readonly setColumnSizing: (columnSizing: ColumnSizingState) => void;
  /** Clears the saved layout so the table goes back to its default columns. */
  readonly restoreDefaults: () => void;
};

/** Returns the conversations table layout saved in the user storage. */
export const useConversationsTableLayout = ({
  isEnabled,
}: UseConversationsTableLayoutOptions):
  | UseConversationsTableLayoutResult
  | undefined => {
  const { data, set } = useServerStorage('user');
  const table = isEnabled ? data.conversations?.table : undefined;
  const density = table?.density ?? 'default';
  const columnOrder = table?.columnOrder;
  const columns = table?.columns;

  const setDensity = useCallback(
    (nextDensity: ConversationsTableDensity) => {
      set({
        conversations: (draft) => {
          draft.table ??= {};
          draft.table.density = nextDensity;
        },
      });
    },
    [set],
  );

  const setIsColumnVisible = useCallback(
    (columnId: string, isVisible: boolean) => {
      set({
        conversations: (draft) => {
          draft.table ??= {};
          draft.table.columns ??= {};
          draft.table.columns[columnId] = {
            ...draft.table.columns[columnId],
            isHidden: !isVisible,
          };
        },
      });
    },
    [set],
  );

  const setIsColumnPinned = useCallback(
    (columnId: string, isPinned: boolean) => {
      set({
        conversations: (draft) => {
          draft.table ??= {};
          draft.table.columns ??= {};
          draft.table.columns[columnId] = {
            ...draft.table.columns[columnId],
            isPinned,
          };
        },
      });
    },
    [set],
  );

  const setColumnOrder = useCallback(
    (
      nextColumnOrder: readonly string[],
      columnId: string,
      isPinned: boolean,
    ) => {
      set({
        conversations: (draft) => {
          draft.table ??= {};
          draft.table.columnOrder = [...nextColumnOrder];

          if (Boolean(draft.table.columns?.[columnId]?.isPinned) !== isPinned) {
            draft.table.columns ??= {};
            draft.table.columns[columnId] = {
              ...draft.table.columns[columnId],
              isPinned,
            };
          }
        },
      });
    },
    [set],
  );

  const setColumnSizing = useCallback(
    (nextColumnSizing: ColumnSizingState) => {
      set({
        conversations: (draft) => {
          draft.table ??= {};
          draft.table.columns ??= {};

          const columnIds = new Set([
            ...Object.keys(draft.table.columns),
            ...Object.keys(nextColumnSizing),
          ]);

          for (const columnId of columnIds) {
            draft.table.columns[columnId] = {
              ...draft.table.columns[columnId],
              width: nextColumnSizing[columnId] ?? null,
            };
          }
        },
      });
    },
    [set],
  );

  const restoreDefaults = useCallback(() => {
    set({
      conversations: (draft) => {
        draft.table = null;
      },
    });
  }, [set]);

  return useMemo(
    () =>
      isEnabled
        ? {
            density,
            setDensity,
            columnOrder,
            columns,
            setIsColumnVisible,
            setIsColumnPinned,
            setColumnOrder,
            setColumnSizing,
            restoreDefaults,
          }
        : undefined,
    [
      isEnabled,
      density,
      setDensity,
      columnOrder,
      columns,
      setIsColumnVisible,
      setIsColumnPinned,
      setColumnOrder,
      setColumnSizing,
      restoreDefaults,
    ],
  );
};
